import { Table, Tbody, Tr, Td } from '@components/core-ui/Table/Table';
import { formatDuration } from '@components/FileUpload/components/dropzone';
import { TFileWithMeta } from '@components/FileUpload/components/dropzone/types/TFileWithMeta';
import { Loader } from '@components/FileUpload/components/Loader';
import { PreviewFile } from '@components/FileUpload/components/PreviewFile';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { mapAttachmentsToIFileWithMeta } from '@pages/User/RepositoryPage/helpers/mapAttachmentsToIFileWithMeta';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { JobsService, PayService } from 'app/API';
import { createFetchRepositoryAudioAttachmentsQueryFn } from 'app/queries/repositories/createFetchRepositoryAudioAttachmentsQueryFn';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { TAudioFile } from 'app/types/entities/TAudioFile';
import { TRepository } from 'app/types/entities/TRepository';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { useMediaAnalysisPolling } from 'app/hooks/useMediaAnalysisPolling';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './BulkSubmitJobsModal/styles/Content';
import { StyledLink } from './BulkSubmitJobsModal/styles/StyledLink';
import { collectConfirmedJobIds } from './BulkSubmitJobsModal/helpers/collectConfirmedJobIds';
import { mapCreatedJobToAnalysisFile } from './BulkSubmitJobsModal/helpers/mapCreatedJobToAnalysisFile';

type TProps = {
  repository: TRepository;
  audioFiles: TAudioFile[];
  organizationId: string;
  repositoryId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

enum BulkJobStep {
  GetAudioDuration = 'retrieving_audio_duration',
  RetrievingPrice = 'retrieving_price',
  RetrievedPrice = 'retrieved_price',
  CreatingDraftJobs = 'creating_draft_jobs',
  DraftJobsCreated = 'draft_jobs_created',
  JobsSubmittedSuccessfully = 'jobs_submitted_successfully',
  JobsSubmittedWithErrors = 'jobs_submitted_with_errors',
}

type RepositoryBulkJobCreationButtonLabel = 'Create Jobs' | 'Submit Jobs' | 'Close';

const showErrorToast = (message: string) => {
  toast.error(message);
};

export const RepositoryBulkJobCreationModal = ({ repositoryId, organizationId, onCancel }: TProps) => {
  const [step, setStep] = useState(BulkJobStep.GetAudioDuration);
  const [buttonLabel, setButtonLabel] = useState<RepositoryBulkJobCreationButtonLabel>(
    'Create Jobs' as RepositoryBulkJobCreationButtonLabel,
  );
  const [removedFileIds, setRemovedFileIds] = useState<(string | undefined)[]>([]);
  const [analysisExtras, setAnalysisExtras] = useState<Record<string, Record<string, unknown>>>({});
  const { call } = useAPI();

  const { me, workspace, organization } = useAuthContext();

  const billingAccount = me?.organizations?.find((o) => o.id === organizationId)?.billingAccount;

  const audioAttachmentsQuery = useQuery({
    queryKey: repositoriesKeys.audioAttachmentsByRepository(repositoryId),
    queryFn: createFetchRepositoryAudioAttachmentsQueryFn(call, {
      onError: showErrorToast,
      dontRedirectOnError: true,
    }),
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (audioAttachmentsQuery.isLoadingError) {
      onCancel();
    }
  }, [audioAttachmentsQuery.isLoadingError, onCancel]);

  const audioAttachments = audioAttachmentsQuery.data;
  const mappedAudioFiles = useMemo(
    () => (audioAttachments ? mapAttachmentsToIFileWithMeta(audioAttachments) : []),
    [audioAttachments],
  );
  const audioFiles = mappedAudioFiles.filter((file) => !removedFileIds.includes(file.meta.Id));
  const attachmentsDuration = (audioAttachments ?? [])
    .map((a) => a.audioDuration || 0)
    .reduce((acc: number, curr: number) => acc + curr, 0);

  const currentStep =
    step === BulkJobStep.GetAudioDuration && audioAttachmentsQuery.isSuccess ? BulkJobStep.RetrievedPrice : step;

  const draftJobsMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      call(
        () =>
          JobsService.bulkCreateRepositoryJobs({
            repositoryId,
            requestBody: { workspaceId },
          }),
        {
          onError: showErrorToast,
          dontRedirectOnError: true,
        },
      ),
    onSuccess: (data) => {
      setAnalysisExtras(Object.fromEntries(data.map((job) => [job.id, { analysisStatus: 'progress' }])));
      setStep(BulkJobStep.DraftJobsCreated);
      setButtonLabel('Submit Jobs');
    },
    onError: () => onCancel(),
  });
  const createdJobs = useMemo(() => draftJobsMutation.data ?? [], [draftJobsMutation.data]);

  const setAnalysisExtra = useCallback((jobId: string, extra: Record<string, unknown>) => {
    setAnalysisExtras((prev) => ({ ...prev, [jobId]: { ...prev[jobId], ...extra } }));
  }, []);
  const analysisFiles = useMemo(
    () => createdJobs.map((job) => mapCreatedJobToAnalysisFile(job, analysisExtras[job.id])),
    [createdJobs, analysisExtras],
  );
  useMediaAnalysisPolling({ files: analysisFiles, setExtra: setAnalysisExtra });

  const getAnalysisDuration = (jobId: string) => Number(analysisExtras[jobId]?.duration) || 0;
  const isAnalyzingAudio = createdJobs.some((job) => analysisExtras[job.id]?.analysisStatus === 'progress');
  const confirmedJobIds = collectConfirmedJobIds(
    createdJobs.map((job) => job.id),
    analysisExtras,
  );
  const unconfirmedJobsCount = createdJobs.length - confirmedJobIds.length;
  const totalAudioDuration =
    createdJobs.length > 0 && !isAnalyzingAudio
      ? confirmedJobIds.reduce((acc, jobId) => acc + getAnalysisDuration(jobId), 0)
      : attachmentsDuration;

  const totalPrice = totalAudioDuration ? (totalAudioDuration / 3600) * (Number(billingAccount?.tierRate) || 0) : 0;

  const submitJobsMutation = useMutation({
    mutationFn: (ids: string[]) =>
      call(() => PayService.payForBulkJobs({ requestBody: { ids } }), {
        onError: showErrorToast,
      }),
    onSuccess: () => setStep(BulkJobStep.JobsSubmittedSuccessfully),
    onError: () => setStep(BulkJobStep.JobsSubmittedWithErrors),
    onSettled: () => setButtonLabel('Close'),
  });

  const isSubmitting = audioAttachmentsQuery.isLoading || draftJobsMutation.isPending || submitJobsMutation.isPending;

  const draftJobCreationHandler = () => {
    if (!workspace?.id) {
      return;
    }
    draftJobsMutation.mutate(workspace.id);
  };

  const jobSubmissionHandler = () => {
    submitJobsMutation.mutate(confirmedJobIds);
  };

  const onRemoveFile = (file: TFileWithMeta) => {
    setRemovedFileIds((current) => [...current, file.meta.Id]);
  };

  return (
    <ModalWrapper width="700px">
      <ModalTitle>Bulk Job Creation</ModalTitle>
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <Content>
        <div>
          {currentStep === BulkJobStep.GetAudioDuration && <div>Retrieving audio duration...</div>}

          {currentStep === BulkJobStep.RetrievedPrice && audioFiles.length === 0 && <div>No audio files found</div>}

          {currentStep === BulkJobStep.RetrievedPrice &&
            audioFiles.map((a) => (
              <PreviewFile key={a.meta.Id} fileWithMeta={a} hideRemoveButton={true} onRemoveFile={onRemoveFile} />
            ))}

          {currentStep === BulkJobStep.DraftJobsCreated ? (
            <div>
              <p className="text-[var(--color-text-muted)] font-[600] text-left text-sm pb-2">Draft jobs created</p>

              {isAnalyzingAudio ? (
                <p className="text-[var(--color-text-muted)] font-[600] text-left text-sm pb-2">Analyzing audio...</p>
              ) : null}

              {!isAnalyzingAudio && unconfirmedJobsCount > 0 ? (
                <p className="text-[var(--color-danger)] text-left text-sm pb-2">
                  {unconfirmedJobsCount === 1
                    ? '1 job could not be analyzed. It will stay as a draft and will not be submitted or charged.'
                    : `${unconfirmedJobsCount} jobs could not be analyzed. They will stay as drafts and will not be submitted or charged.`}
                </p>
              ) : null}

              {createdJobs.map((job) => (
                <div className="flex my-2 items-center text-sm" key={job.id}>
                  <StyledLink
                    to={routes.jobEdit.make(organization?.slug ?? '', workspace?.slug ?? '', job.id)}
                    className="mr-2 max-w-full text-ellipsis overflow-hidden"
                  >
                    {job.name}
                  </StyledLink>

                  {analysisExtras[job.id]?.analysisStatus === 'progress' ? (
                    <Loader size={14} />
                  ) : analysisExtras[job.id]?.analysisStatus === 'done' ? (
                    <div className="flex items-center text-nowrap">
                      <div className="mr-2">[{formatDuration(getAnalysisDuration(job.id))},</div>
                      <div className="text-sm font-[600] text-right py-2">
                        ${((getAnalysisDuration(job.id) / 3600) * (Number(billingAccount?.tierRate) || 0)).toFixed(2)}
                      </div>
                      ]
                    </div>
                  ) : (
                    <div className="text-[var(--color-danger)] text-[0.775rem]">
                      {String(analysisExtras[job.id]?.analysisErrorMessage ?? 'Analysis error')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {currentStep === BulkJobStep.JobsSubmittedSuccessfully ? <div>Jobs submitted successfully</div> : null}

          {currentStep === BulkJobStep.JobsSubmittedWithErrors ? (
            <div className="text-[var(--color-danger)]">Jobs submitted with errors, please check job list</div>
          ) : null}
        </div>

        {audioFiles.length > 0 ? (
          <Table className="text-left text-xs border-collapse">
            <Tbody>
              <Tr className="border-b border-solid border-[var(--color-divider)]">
                <Td className="text-[var(--color-text-muted)] font-[600] py-2">Total duration</Td>
                <Td className="text-sm text-right py-2">
                  {totalAudioDuration ? formatDuration(totalAudioDuration) : <i>Loading...</i>}
                </Td>
              </Tr>
              <Tr>
                <Td className="text-[var(--color-text-muted)] font-[600] py-2">Total price for bulk job</Td>
                <Td className="text-sm font-[600] text-right py-2">${totalPrice.toFixed(2)}</Td>
              </Tr>
            </Tbody>
          </Table>
        ) : null}

        <div className="buttonContainer">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            className="ml-4"
            onClick={() => {
              if (currentStep === BulkJobStep.RetrievedPrice) {
                return draftJobCreationHandler();
              }

              if (currentStep === BulkJobStep.DraftJobsCreated) {
                return jobSubmissionHandler();
              }

              return onCancel();
            }}
            disabled={
              isSubmitting || (buttonLabel === 'Submit Jobs' && (isAnalyzingAudio || confirmedJobIds.length === 0))
            }
          >
            {buttonLabel}
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
